/**
 * pending-credit-resume — sessionStorage utility for resuming the interrupted
 * flow after a credit pack purchase.
 *
 * cas-d535: when the CreditsExhaustedDialog routes the user to Stripe Checkout,
 * we stash the originating page path in sessionStorage. After the purchase
 * succeeds (/billing/packs/success), the page reads this stash and navigates
 * back so the user can retry the blocked action without re-navigating from
 * scratch.
 *
 * Mirrors the pending-promo.ts pattern:
 *   stash → stashCreditResume(returnPath, workspaceUid)
 *   consume (read+clear) → consumeCreditResume()
 *   explicit clear → clearCreditResume()
 *
 * Shape: { returnPath: string, workspaceUid: string, stashedAt: number }
 * TTL: 30 min — Stripe checkout completes well within this window.
 * Storage: sessionStorage (not localStorage) — tab-scoped, no cross-tab bleed.
 * Key: "pendingCreditResumeUrl"
 */

export const PENDING_CREDIT_RESUME_KEY = 'pendingCreditResumeUrl';
export const PENDING_CREDIT_RESUME_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface PendingCreditResume {
  returnPath: string;
  workspaceUid: string;
  stashedAt: number; // ms epoch
}

/** Resolved resume entry returned to callers. */
export interface CreditResumeEntry {
  returnPath: string;
  workspaceUid: string;
}

const isBrowser = (): boolean => typeof window !== 'undefined' && !!window.sessionStorage;

/**
 * Stash the originating page path before handing off to Stripe Checkout so the
 * user can be returned to it after a successful pack purchase. Overwrites any
 * existing stash. SSR-safe (no-op when window is undefined).
 *
 * `returnPath` should be `window.location.pathname + window.location.search`
 * (same-origin relative path). `workspaceUid` is used for logging only — the
 * success page navigates to the path regardless of workspace match because the
 * workspace can't change mid-session-storage lifetime.
 */
export function stashCreditResume(returnPath: string, workspaceUid: string): void {
  if (!isBrowser()) return;
  const data: PendingCreditResume = {
    returnPath,
    workspaceUid,
    stashedAt: Date.now()
  };
  window.sessionStorage.setItem(PENDING_CREDIT_RESUME_KEY, JSON.stringify(data));
}

/**
 * Read and immediately clear the pending resume entry. Returns null when
 * absent, expired, or malformed. Always clears from storage on call so a
 * later reload never re-triggers the resume navigation.
 * SSR-safe.
 */
export function consumeCreditResume(): CreditResumeEntry | null {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_CREDIT_RESUME_KEY);
    // Always clear — even on parse error, expired entry, etc.
    window.sessionStorage.removeItem(PENDING_CREDIT_RESUME_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as PendingCreditResume;
    if (!data?.returnPath || !data?.workspaceUid) return null;
    if (Date.now() - data.stashedAt > PENDING_CREDIT_RESUME_TTL_MS) return null;
    return { returnPath: data.returnPath, workspaceUid: data.workspaceUid };
  } catch {
    // Malformed JSON — clear already happened above; return null.
    return null;
  }
}

/**
 * Remove the resume stash without reading it. SSR-safe. Safe to call when
 * nothing is stashed.
 */
export function clearCreditResume(): void {
  if (!isBrowser()) return;
  window.sessionStorage.removeItem(PENDING_CREDIT_RESUME_KEY);
}
